/**
 * FinancialsTable Component
 *
 * Displays balance sheet data in a multi-period table format.
 * Supports ACTIVOS, PASIVOS, PATRIMONIO, and RESULTADOS tabs.
 * Period mode (annual/quarterly/monthly) is controlled via props.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchFinancialsWithMode,
  exportFinancialsMonthly,
  formatFinancialValue,
  formatPeriodHeader,
  type FinancialsData,
  type FinancialsRow,
} from '../../../services/analystBenchService';
import type { PeriodMode } from '../../../types/periodMode';
import { PERIOD_MODE_CONFIG } from '../../../types/periodMode';
import PeriodModeSelector from '../PeriodModeSelector';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import './AnalystBenchTable.css';

interface FinancialsTableProps {
  institutionId: string;
  periodMode: PeriodMode;
  onPeriodModeChange: (mode: PeriodMode) => void;
}

type FinancialsTab = 'activos' | 'pasivos' | 'patrimonio' | 'resultados';

const TAB_KEYS: Record<FinancialsTab, string> = {
  activos: 'financials.activos',
  pasivos: 'financials.pasivos',
  patrimonio: 'financials.patrimonio',
  resultados: 'financials.resultados',
};

export default function FinancialsTable({
  institutionId,
  periodMode,
  onPeriodModeChange,
}: FinancialsTableProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<FinancialsTab>('activos');
  const [data, setData] = useState<FinancialsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function loadData() {
      if (!institutionId) return;

      setLoading(true);
      setError(null);

      try {
        const result = await fetchFinancialsWithMode(institutionId, periodMode);
        setData(result);
      } catch (err) {
        console.error('Error loading financials:', err);
        setError(err instanceof Error ? err.message : 'Error loading data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [institutionId, periodMode]);

  const renderTable = (rows: FinancialsRow[], periods: string[]) => {
    if (!rows || rows.length === 0) {
      return <EmptyState message={t('financials.noData')} />;
    }

    return (
      <div className="abt-table-container">
        <table className="abt-table">
          <thead>
            <tr>
              <th className="abt-sticky-col abt-header-cell">{t('financials.cuenta')}</th>
              {periods.map((period) => (
                <th key={period} className="abt-header-cell abt-period-cell">
                  {formatPeriodHeader(period)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.code}
                className={`abt-row ${row.indent === 0 ? 'abt-row-total' : ''}`}
              >
                <td className="abt-sticky-col abt-account-cell">
                  <div
                    className="abt-account-inner"
                    style={{ paddingLeft: `${12 + row.indent * 16}px` }}
                  >
                    <span className="abt-account-code">{row.code}</span>
                    <span className="abt-account-name">{row.name}</span>
                  </div>
                </td>
                {periods.map((period) => (
                  <td key={period} className="abt-value-cell">
                    {formatFinancialValue(row.values[period])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="financials-table">
        <div className="abt-page-header">
          <div className="abt-page-header-top">
            <h2 className="abt-view-title">{t('financials.title')}</h2>
            <PeriodModeSelector mode={periodMode} onChange={onPeriodModeChange} disabled />
          </div>
          <div className="abt-underline-tabs">
            {(Object.keys(TAB_KEYS) as FinancialsTab[]).map((tab) => (
              <button key={tab} className={`abt-underline-tab ${activeTab === tab ? 'active' : ''}`} disabled>
                {t(TAB_KEYS[tab])}
              </button>
            ))}
          </div>
        </div>
        <LoadingSpinner label={t('financials.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="financials-table">
        <div className="abt-page-header">
          <div className="abt-page-header-top">
            <h2 className="abt-view-title">{t('financials.title')}</h2>
            <PeriodModeSelector mode={periodMode} onChange={onPeriodModeChange} />
          </div>
          <div className="abt-underline-tabs">
            {(Object.keys(TAB_KEYS) as FinancialsTab[]).map((tab) => (
              <button key={tab} className={`abt-underline-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                {t(TAB_KEYS[tab])}
              </button>
            ))}
          </div>
        </div>
        <div className="abt-error">
          <p>{error}</p>
          <button
            className="abt-retry-btn"
            onClick={() => {
              setLoading(true);
              setError(null);
              fetchFinancialsWithMode(institutionId, periodMode)
                .then(setData)
                .catch((err) => setError(err.message))
                .finally(() => setLoading(false));
            }}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="financials-table">
        <EmptyState />
      </div>
    );
  }

  const currentRows = data.sections[activeTab];
  const modeConfig = PERIOD_MODE_CONFIG[periodMode];

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportFinancialsMonthly(institutionId, data.institutionName);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="financials-table">
      <div className="abt-page-header">
        <div className="abt-page-header-top">
          <h2 className="abt-view-title">{t('financials.title')}</h2>
          <div className="abt-mode-info">
            <span className="abt-mode-description">{t(modeConfig.description)}</span>
            <PeriodModeSelector
              mode={periodMode}
              onChange={onPeriodModeChange}
            />
            <button
              className="abt-export-btn"
              onClick={handleExport}
              disabled={exporting}
              title="Export monthly data as CSV"
            >
              {exporting ? (
                <span className="abt-export-spinner" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        <div className="abt-underline-tabs">
          {(Object.keys(TAB_KEYS) as FinancialsTab[]).map((tab) => (
            <button
              key={tab}
              className={`abt-underline-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {t(TAB_KEYS[tab])}
            </button>
          ))}
        </div>
      </div>
      {renderTable(currentRows, data.periods)}
    </div>
  );
}
